/**
 * PlayerPanel — the rectangular zone for one player on the active board.
 *
 * Layout (top → bottom for P1; reversed for P2 via parent component):
 *   1. Equipment row: CHAMP, EQUIP ▲, DEFENSE, (optional Stance), SIG×3
 *   2. Player bar (name / HP / stamina / stance pill / deck-discard counts)
 *
 * The player's HAND is NOT rendered inside this panel — it lives in the
 * FloatingHand overlay which attaches to the screen edge so the cards float
 * "in your hand" outside the board view.
 *
 * Active player gets a thicker amber border.
 */
import { useState } from "react";
import type { Card } from "@/types/card";
import type { PlayerState } from "@/types/game";
import { BoardCard } from "./BoardCard";
import { PlayerBar } from "./PlayerBar";
import { FloatingHand } from "./FloatingHand";
import { cn } from "@/lib/utils";

/**
 * Click on a card *on the board* (not in hand). Parent decides which
 * source-specific action popup to open.
 *   kind "sig"      → a signature-move slot card; slotIdx is the sig index.
 *   kind "equip"    → an equipment slot card; slotIdx is the equip index.
 *   kind "armor"    → the armor slot card; slotIdx is undefined.
 *   kind "champion" → the player's champion card; slotIdx is undefined.
 *   kind "stance"   → the player's stance card; slotIdx is undefined.
 *   kind "played"   → a card sitting in the Played Cards zone; slotIdx is undefined.
 *   kind "item"     → a utility "Item" in the Items zone; slotIdx is undefined.
 *
 * The first three kinds open the popup with active-play buttons (ATK, DEF,
 * Block, etc.). The last four are view-only today — the popup shows full
 * card details with no buttons, ready for a future "Activate Ability" hook.
 */
export type SlotClickKind =
  | "sig"
  | "equip"
  | "armor"
  | "champion"
  | "stance"
  | "played"
  | "item";

interface PlayerPanelProps {
  player: PlayerState;
  cardMap: Map<string, Card>;
  isActive: boolean;
  /** "top" panel for P2 puts hand at top; "bottom" panel for P1 at bottom. */
  side: "top" | "bottom";
  onAdjustHealth: (delta: number) => void;
  onAdjustStamina: (delta: number) => void;
  /** Click on a hand card — parent opens the CardActionPopup. */
  onHandCardClick?: (instanceId: string, card: Card) => void;
  /** Click on a sig/equip/armor/champion/stance/played/item card on the
   *  board — parent opens the action popup. */
  onSlotCardClick?: (
    kind: SlotClickKind,
    card: Card,
    slotIdx?: number,
    instanceId?: string,
  ) => void;
  /** Whether the armor slot should pulse blue (defender window + non-piercing attack incoming). */
  armorBlockable?: boolean;
  /** Click on the Deck count pill — draws a card. */
  onDrawCard?: () => void;
  /** Click on the Discard count pill — opens a viewer of all discarded cards. */
  onOpenDiscardPile?: () => void;
  /** Expend ONE token instance (popup closes after; user re-clicks to expend more). */
  onExpendToken?: (instanceId: string, cardDbId: string) => void;
  /** Pass-and-play: render this player's hand face-down (the viewer is the
   *  opposing player). The board, equipment, and signature slots stay visible
   *  (those are public information). */
  hideHand?: boolean;
  /** Tabletop-mode "flip my hand" toggle. When set, FloatingHand renders a
   *  reveal control on the face-down stack and a Hide button when face-up,
   *  letting THIS player flip their own hand independently of viewer logic.
   *  Pass undefined to use the legacy viewer-driven hideHand behavior. */
  onToggleSelfReveal?: () => void;
  /** Tabletop-mode rotation flag. When true, FloatingHand renders rotated
   *  180° so the seated top player reads cards right-side-up. */
  handRotated?: boolean;
  /** Tabletop-mode internal-layout flag. When true, this panel uses the
   *  bottom-style internal stacking (bar → equip → zones) regardless of
   *  whether `side === "top"`. The wrapper rotation in PlaytestBoard then
   *  flips it visually so the seated opponent sees the same layout the
   *  bottom player sees from their seat. Without this, the top panel
   *  inverts its rows AND gets rotated, double-flipping the layout. */
  mirrorAsBottom?: boolean;
}

/**
 * Group token instances by their underlying card id so the visual stack shows
 * one card per kind with a count badge. Click → popup with Expend button.
 */
function groupByCardId(
  entries: { instanceId: string; card: Card }[],
): { card: Card; instanceIds: string[] }[] {
  const map = new Map<string, { card: Card; instanceIds: string[] }>();
  for (const e of entries) {
    const k = e.card.id;
    const existing = map.get(k);
    if (existing) existing.instanceIds.push(e.instanceId);
    else map.set(k, { card: e.card, instanceIds: [e.instanceId] });
  }
  return Array.from(map.values());
}

export function PlayerPanel({
  player,
  cardMap,
  isActive,
  side,
  onAdjustHealth,
  onAdjustStamina,
  onHandCardClick,
  onSlotCardClick,
  armorBlockable = false,
  onDrawCard,
  onOpenDiscardPile,
  onExpendToken,
  hideHand = false,
  onToggleSelfReveal,
  handRotated = false,
  mirrorAsBottom = false,
}: PlayerPanelProps) {
  /** Token-stack popup: shown when a token stack is clicked. Two-step UX:
   *  click Expend → confirm overlay → confirm → expend ONE token → popup closes. */
  const [tokenStackPopup, setTokenStackPopup] = useState<{
    card: Card;
    instanceIds: string[];
  } | null>(null);
  const [tokenConfirm, setTokenConfirm] = useState(false);

  // Build the slot card list. Champion + armor are computed; equipment +
  // signature moves come from equipSlots in order. Stance comes from the
  // player.stanceCardId. Signature moves are detected by cardType.
  const champion = player.championCardId ? cardMap.get(player.championCardId) ?? null : null;
  const armor = player.armorCardId ? cardMap.get(player.armorCardId) ?? null : null;
  const stance = player.stanceCardId ? cardMap.get(player.stanceCardId) ?? null : null;

  // Equipment row: list of { card, slotIdx, tapped, disarmed } for each filled slot
  const equipEntries: { card: Card; slotIdx: number; tapped: boolean; disarmed: boolean }[] = [];
  player.equipSlots.forEach((slot, idx) => {
    if (!slot.cardId) return;
    const c = cardMap.get(slot.cardId);
    if (!c) return;
    if (c.cardType === "Signature Move") return;
    equipEntries.push({
      card: c,
      slotIdx: idx,
      tapped: slot.tapped,
      disarmed: slot.disarmed === true,
    });
  });

  // Signature Moves — from player.signatureMoveIds, minus already-used ones.
  const sigCards: Card[] = (player.signatureMoveIds ?? [])
    .filter((id) => !(player.signatureMovesUsed ?? []).includes(id))
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c));

  // Use handInstances when present (per-instance ids); fall back to hand[].
  const handItems: { instanceId: string; card: Card }[] = (
    player.handInstances && player.handInstances.length > 0
      ? player.handInstances
          .map((h) => {
            const c = cardMap.get(h.cardId);
            return c ? { instanceId: h.id, card: c } : null;
          })
          .filter((x): x is { instanceId: string; card: Card } => Boolean(x))
      : player.hand
          .map((id, i) => {
            const c = cardMap.get(id);
            return c ? { instanceId: `${id}_${i}`, card: c } : null;
          })
          .filter((x): x is { instanceId: string; card: Card } => Boolean(x))
  );

  // Build the rendered equipment row dynamically. We show one slot per
  // existing card per kind, plus a single empty placeholder for kinds that
  // haven't been filled yet (champ, equip, armor) so the row reads as a
  // canonical layout. Stance and signature slots only appear when a card
  // exists.
  type RowItem = {
    label: string;
    chevron?: boolean;
    card: Card | null;
    kind: string;
    onClick?: () => void;
    muted?: boolean;
    cornerBadge?: string;
    blueHue?: boolean;
    wearCount?: number;
    bloodCount?: number;
    disarmed?: boolean;
  };
  const equipRowItems: RowItem[] = [];
  if (champion)
    equipRowItems.push({
      label: "CHAMP",
      card: champion,
      kind: "champ",
      onClick: onSlotCardClick
        ? () => onSlotCardClick("champion", champion)
        : undefined,
    });
  for (const e of equipEntries) {
    equipRowItems.push({
      label: "EQUIP",
      chevron: true,
      card: e.card,
      kind: "equip",
      muted: e.tapped || e.disarmed,
      cornerBadge: e.disarmed ? undefined : (e.tapped ? "TAP" : undefined),
      disarmed: e.disarmed,
      onClick: onSlotCardClick
        ? () => onSlotCardClick("equip", e.card, e.slotIdx)
        : undefined,
    });
  }
  if (armor)
    equipRowItems.push({
      label: "DEFENSE",
      card: armor,
      kind: "armor",
      cornerBadge: armorBlockable ? "BLOCK" : undefined,
      onClick: onSlotCardClick ? () => onSlotCardClick("armor", armor) : undefined,
      blueHue: armorBlockable,
      wearCount: player.armorWearCounters,
      bloodCount: armor.name === "Bloodforged Plate" ? player.armorBloodCounters : undefined,
    });
  if (stance)
    equipRowItems.push({
      label: "STANCE",
      card: stance,
      kind: "stance",
      cornerBadge: "STN",
      onClick: onSlotCardClick
        ? () => onSlotCardClick("stance", stance)
        : undefined,
    });
  // Sig moves with their slot index (so the popup can call playSig).
  // CRITICAL: engine.playSig reads `player.sigMoveSlots[slotIdx]`, NOT
  // equipSlots. Iterate sigMoveSlots directly so the slot index passed to the
  // popup actually matches the engine's lookup.
  (player.sigMoveSlots ?? []).forEach((sigSlot, idx) => {
    if (sigSlot.used) return; // hide spent sigs
    const c = cardMap.get(sigSlot.cardId);
    if (!c) return;
    equipRowItems.push({
      label: "SIG",
      card: c,
      kind: "sig",
      muted: sigSlot.inPlay,
      cornerBadge: sigSlot.inPlay ? "PLAY" : undefined,
      onClick: onSlotCardClick ? () => onSlotCardClick("sig", c, idx) : undefined,
    });
  });
  // Legacy fallback — surface any sig from signatureMoveIds that didn't make
  // it into sigMoveSlots (very unlikely, but kept defensive). These get NO
  // slotIdx, so the popup's playSig call will fail gracefully.
  if (!player.sigMoveSlots || player.sigMoveSlots.length === 0) {
    for (const c of sigCards) {
      equipRowItems.push({
        label: "SIG",
        card: c,
        kind: "sig",
        onClick: onSlotCardClick ? () => onSlotCardClick("sig", c) : undefined,
      });
    }
  }

  const equipRow = (
    <div className="flex items-end gap-2 overflow-x-auto px-3 py-2 sm:flex-wrap sm:overflow-visible">
      {equipRowItems.length === 0 && (
        <span className="text-xs text-[#64748b]">No equipment placed.</span>
      )}
      {equipRowItems.map((item, i) => (
        <div key={`${item.label}_${i}`} className="shrink-0">
          <BoardCard
            card={item.card}
            size="slot"
            slotLabel={item.label}
            slotChevron={item.chevron}
            cornerBadge={item.cornerBadge}
            muted={item.muted}
            blueHue={item.blueHue}
            wearCount={item.wearCount}
            bloodCount={item.bloodCount}
            disarmed={item.disarmed}
            hidePills
            onClick={item.onClick}
          />
        </div>
      ))}
    </div>
  );

  // ─── Three persistent zones rendered as one strip ────────────────────
  //   Played Cards → Items → Tokens
  // Played Cards: dimmed copies of every non-AD card played this round
  //               (sigs, utilities, etc.). Cleared at endRound.
  // Items:        utility "Item" sub-type cards that persist on the field.
  // Tokens:       token instances (Focus, Fortify, etc.).
  const playedItems: { instanceId: string; card: Card }[] = (player.playedCards ?? [])
    .map((p) => {
      const c = cardMap.get(p.cardId);
      return c ? { instanceId: p.id, card: c } : null;
    })
    .filter((x): x is { instanceId: string; card: Card } => Boolean(x));

  const itemEntries: { instanceId: string; card: Card }[] = (player.utilityInPlay ?? [])
    .map((u) => {
      const c = cardMap.get(u.cardId);
      // "Item" utility sub-type goes in this row; non-Item utilities (Action,
      // Reaction, etc.) only appear in the Played Cards zone.
      if (!c) return null;
      const sub = (c.utilityFields?.utilityType ?? "").toLowerCase();
      if (sub !== "item") return null;
      return { instanceId: u.id, card: c };
    })
    .filter((x): x is { instanceId: string; card: Card } => Boolean(x));

  const tokenItems: { instanceId: string; card: Card }[] = player.tokenArea
    .map((t) => {
      const c = cardMap.get(t.cardId);
      return c ? { instanceId: t.id, card: c } : null;
    })
    .filter((x): x is { instanceId: string; card: Card } => Boolean(x));

  /**
   * Render a zone of full card images. Played/Items zones show one card per
   * instance at 60% opacity. Tokens stack by card-id with a count badge and
   * are clickable to open the Expend popup.
   *
   * `onCardClick` (when provided, non-token zones only) opens the read-only
   * CardActionPopup so the player can inspect the card's full details.
   */
  const renderImageZone = (
    label: string,
    entries: { instanceId: string; card: Card }[],
    opts: {
      dimmed?: boolean;
      isToken?: boolean;
      onCardClick?: (instanceId: string, card: Card) => void;
    } = {},
  ) => {
    // Tokens stack by cardId; other zones list one entry per instance.
    const groups = opts.isToken
      ? groupByCardId(entries)
      : entries.map((e) => ({ card: e.card, instanceIds: [e.instanceId] }));
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-[9px] uppercase tracking-wider text-[#64748b]">
          {label}
        </span>
        <div className="flex min-h-[3.5rem] flex-wrap items-center gap-1.5">
          {groups.length === 0 ? (
            <span className="text-[10px] italic text-[#475569]">—</span>
          ) : (
            groups.map(({ card: c, instanceIds }) => (
              <div
                key={`${c.id}_${instanceIds[0]}`}
                className="relative shrink-0"
              >
                <BoardCard
                  card={c}
                  size="slot"
                  hidePills
                  className={cn(opts.dimmed && "opacity-60")}
                  onClick={
                    opts.isToken
                      ? () => setTokenStackPopup({ card: c, instanceIds })
                      : opts.onCardClick
                        ? () => opts.onCardClick!(instanceIds[0], c)
                        : undefined
                  }
                />
                {opts.isToken && instanceIds.length > 1 && (
                  <span className="pointer-events-none absolute -right-1 -top-1 z-10 rounded-full border border-[#0e1014] bg-amber-soft px-1.5 py-0.5 text-[9px] font-black text-black shadow">
                    ×{instanceIds.length}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const zonesRow = (
    <div className="grid grid-cols-3 gap-2 border-t border-[#2a2f3a] px-3 py-1.5">
      {renderImageZone("Played Cards", playedItems, {
        dimmed: true,
        onCardClick: onSlotCardClick
          ? (iid, c) => onSlotCardClick("played", c, undefined, iid)
          : undefined,
      })}
      {renderImageZone("Items", itemEntries, {
        dimmed: true,
        onCardClick: onSlotCardClick
          ? (iid, c) => onSlotCardClick("item", c, undefined, iid)
          : undefined,
      })}
      {renderImageZone("Tokens", tokenItems, { isToken: true })}
    </div>
  );

  const bar = (
    <PlayerBar
      player={player}
      cardMap={cardMap}
      isActive={isActive}
      onAdjustHealth={onAdjustHealth}
      onAdjustStamina={onAdjustStamina}
      onDrawCard={onDrawCard}
      onOpenDiscardPile={onOpenDiscardPile}
    />
  );

  // The hand floats at the screen edge — rendered below the panel chrome.
  const playerNum: 1 | 2 = side === "top" ? 2 : 1;
  const floatingHand = (
    <FloatingHand
      playerNum={playerNum}
      playerName={player.name}
      handItems={handItems}
      side={side}
      onCardClick={(iid, card) => onHandCardClick?.(iid, card)}
      hidden={hideHand}
      onToggleSelfReveal={onToggleSelfReveal}
      rotated={handRotated}
    />
  );

  return (
    <div
      className={cn(
        "rounded-md border bg-[#13151a] transition-colors",
        isActive
          ? "border-amber-soft shadow-[0_0_0_1px_rgba(245,158,11,0.4)]"
          : "border-[#2a2f3a]",
      )}
    >
      {/* For top-side (P2) in stacked mode: zones, equipment, bar (hand floats at top of screen)
          For bottom-side (P1): bar, equipment, zones (hand floats at bottom of screen)
          For tabletop top: use bottom layout — wrapper handles 180° rotation. */}
      {side === "top" && !mirrorAsBottom ? (
        <>
          {/* P2 stacked: zones sit ABOVE the champion/equipment line. */}
          {zonesRow}
          {equipRow}
          {bar}
        </>
      ) : (
        <>
          {/* P1, or tabletop P2 (rotated by wrapper): zones sit BELOW. */}
          {bar}
          {equipRow}
          {zonesRow}
        </>
      )}
      {floatingHand}
      {tokenStackPopup && (
        <TokenStackPopup
          card={tokenStackPopup.card}
          count={tokenStackPopup.instanceIds.length}
          confirming={tokenConfirm}
          onExpendClick={() => setTokenConfirm(true)}
          onConfirm={() => {
            const id = tokenStackPopup.instanceIds[0];
            onExpendToken?.(id, tokenStackPopup.card.id);
            setTokenStackPopup(null);
            setTokenConfirm(false);
          }}
          onCancel={() => setTokenConfirm(false)}
          onClose={() => {
            setTokenStackPopup(null);
            setTokenConfirm(false);
          }}
        />
      )}
    </div>
  );
}

// ---- Token stack detail popup ---------------------------------------------

function TokenStackPopup({
  card,
  count,
  confirming,
  onExpendClick,
  onConfirm,
  onCancel,
  onClose,
}: {
  card: Card;
  count: number;
  confirming: boolean;
  onExpendClick: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-lg border border-[#2a2f3a] bg-[#13151a] p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0">
            <BoardCard card={card} size="prep" hidePills />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-sm font-bold text-white">{card.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#94a3b8]">
              {card.cardType} · ×{count}
            </p>
            {card.tokenFields?.ability && (
              <p className="text-[11px] leading-snug text-[#cbd5e1]">
                {card.tokenFields.ability}
              </p>
            )}
            {card.utilityFields?.ability && (
              <p className="text-[11px] leading-snug text-[#cbd5e1]">
                {card.utilityFields.ability}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {confirming ? (
            <>
              <span className="mr-auto text-[11px] text-amber-soft">
                Expend one {card.name}?
              </span>
              <button
                type="button"
                onClick={onCancel}
                className="rounded border border-[#2a2f3a] bg-[#1a1d24] px-3 py-1 text-xs text-[#94a3b8] hover:bg-[#22262f]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                className="rounded border border-amber-soft/60 bg-amber-500/30 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-soft hover:bg-amber-500/50"
              >
                Confirm
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-[#2a2f3a] bg-[#1a1d24] px-3 py-1 text-xs text-[#94a3b8] hover:bg-[#22262f]"
              >
                Close
              </button>
              <button
                type="button"
                onClick={onExpendClick}
                className="rounded border border-amber-soft/60 bg-amber-500/20 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-soft hover:bg-amber-500/40"
              >
                Expend
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
